import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  Gavel,
  Heart,
  Package,
  Plus,
  Store,
  ArrowUpRight,
  TrendingUp,
  CreditCard,
  Trophy,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { StripeConnectCard } from "@/components/stripe-connect-card";
import { useAnimatedCount } from "@/hooks/use-animated-count";
import { Countdown } from "@/components/countdown";
import { StatusBadge } from "@/components/status-badge";
import { GradeBadge } from "@/components/grade-badge";
import { formatGBP } from "@/lib/format";
import { buildAuctionSlug } from "@/lib/auction-slug";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — BidNova Auctions" }] }),
  component: DashboardPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type StatTo = "/my-bids" | "/watchlist" | "/my-auctions" | "/orders";

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  to,
  accent = "primary",
}: {
  icon: typeof Gavel;
  label: string;
  value: number;
  hint: string;
  to: StatTo;
  accent?: "primary" | "gold" | "emerald" | "sky" | "amber";
}) {
  const animated = useAnimatedCount(value);
  const tone = {
    primary: "from-primary/20 to-primary/0 text-primary",
    gold: "from-gold/20 to-gold/0 text-gold",
    emerald: "from-emerald-500/20 to-emerald-500/0 text-emerald-400",
    sky: "from-sky-500/20 to-sky-500/0 text-sky-400",
    amber: "from-amber-500/20 to-amber-500/0 text-amber-400",
  }[accent];

  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05] hover:shadow-[0_20px_60px_-20px_rgba(139,92,246,0.35)]"
    >
      <div className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br ${tone} blur-2xl opacity-60 transition-opacity duration-500 group-hover:opacity-100`} />
      <div className="relative flex items-center justify-between">
        <div className={`rounded-xl border border-white/10 bg-white/5 p-2.5 backdrop-blur-sm ${tone.split(" ").pop()}`}>
          <Icon className="h-4 w-4" />
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground/40 transition-all duration-300 group-hover:text-foreground group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
      <div className="relative mt-6">
        <div className="font-display text-4xl tracking-tight tabular-nums">
          {Math.round(animated).toLocaleString()}
        </div>
        <p className="mt-1 text-sm font-medium text-foreground/80">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
    </Link>
  );
}

type AuctionRow = {
  id: string;
  title: string;
  images: string[] | null;
  end_time: string;
  status: string;
  current_bid_pence: number | null;
  starting_price_pence: number;
  high_bidder_id: string | null;
  grading_company: string | null;
  grade: string | null;
  card_type: string | null;
  product_type: string | null;
  accessory_category: string | null;
  custom_grading_company: string | null;
  custom_grade: string | null;
  reservation_expires_at: string | null;
};

function useBuyerData(uid: string | undefined) {
  return useQuery({
    queryKey: ["buyer-dashboard", uid],
    enabled: !!uid,
    staleTime: 15_000,
    queryFn: async () => {
      const cols =
        "id,title,images,end_time,status,current_bid_pence,starting_price_pence,high_bidder_id,grading_company,grade,card_type,product_type,accessory_category,custom_grading_company,custom_grade,reservation_expires_at";
      const [myBids, watchRows, listings, orders] = await Promise.all([
        // Auctions the user has bid on (their own bid rows are always readable).
        supabase
          .from("bids")
          .select("auction_id")
          .eq("bidder_id", uid!)
          .order("created_at", { ascending: false })
          .limit(200),
        supabase.from("watchlist").select("auction_id").eq("user_id", uid!).limit(200),
        supabase.from("auctions").select("id", { count: "exact", head: true }).eq("seller_id", uid!),
        supabase
          .from("orders")
          .select("id,status,amount_pence,auction_id,created_at")
          .eq("buyer_id", uid!)
          .order("created_at", { ascending: false })
          .limit(100),
      ]);

      const bidIds = [...new Set((myBids.data ?? []).map((b) => b.auction_id))];
      const watchIds = (watchRows.data ?? []).map((w) => w.auction_id);
      const ids = [...new Set([...bidIds, ...watchIds])];

      let auctions: AuctionRow[] = [];
      if (ids.length) {
        const { data } = await supabase.from("auctions").select(cols).in("id", ids);
        auctions = (data ?? []) as unknown as AuctionRow[];
      }

      const byId = new Map(auctions.map((a) => [a.id, a]));
      const bidAuctions = bidIds.map((i) => byId.get(i)).filter(Boolean) as AuctionRow[];
      const watchAuctions = watchIds.map((i) => byId.get(i)).filter(Boolean) as AuctionRow[];

      const now = Date.now();
      const isLive = (a: AuctionRow) =>
        a.status === "active" && new Date(a.end_time).getTime() > now;

      const leading = bidAuctions.filter((a) => isLive(a) && a.high_bidder_id === uid);
      const outbid = bidAuctions.filter((a) => isLive(a) && a.high_bidder_id !== uid);
      const won = bidAuctions.filter((a) => a.status === "sold" && a.high_bidder_id === uid);

      const orderRows = orders.data ?? [];
      const pendingPayments = orderRows.filter((o) => o.status === "pending_payment");
      const spendStatuses = ["paid", "ready_to_ship", "exported_to_click_drop", "shipped", "delivered", "released"];
      const totalSpendPence = orderRows
        .filter((o) => spendStatuses.includes(o.status))
        .reduce((sum, o) => sum + (o.amount_pence ?? 0), 0);

      return {
        leading,
        outbid,
        won,
        watchAuctions,
        listings: listings.count ?? 0,
        orders: orderRows,
        pendingPayments,
        totalSpendPence,
      };
    },
  });
}

function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation("settings");
  const name =
    (user?.user_metadata?.display_name as string | undefined) ??
    user?.email?.split("@")[0] ??
    "collector";

  const { data, refetch } = useBuyerData(user?.id);

  // Realtime: keep the buyer view honest when bids land or orders progress.
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`buyer-dashboard-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "auctions" }, () => refetch())
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `buyer_id=eq.${user.id}` },
        () => refetch(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user, refetch]);

  return (
    <div className="relative">
      {/* Ambient background glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-primary/[0.06] via-transparent to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
        <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">
              {t("dashboard.welcomeBack", "Welcome back")}
            </p>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl">
              <span className="text-gold-gradient">{name}</span>
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-md">
              {t("dashboard.subtitle", "Your live auction control centre. Track bids, watchlist and payouts in one place.")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild className="bg-gold-gradient hover:opacity-90 shadow-[0_10px_30px_-10px_rgba(201,162,75,0.5)]">
              <Link to="/seller">
                <Store className="h-4 w-4 mr-2" /> {t("dashboard.sellerDashboardBtn", "Seller Dashboard")}
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-white/10 bg-white/5 backdrop-blur-xl hover:bg-white/10">
              <Link to="/sell">
                <Plus className="h-4 w-4 mr-2" /> {t("dashboard.listCardBtn", "List a card")}
              </Link>
            </Button>
          </div>
        </header>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Gavel}
            label={t("dashboard.stat.leadingLabel", "Leading now")}
            value={data?.leading.length ?? 0}
            hint={t("dashboard.stat.leadingHint", "Live auctions you're winning")}
            to="/my-bids"
            accent="emerald"
          />
          <StatCard
            icon={TriangleAlert}
            label={t("dashboard.stat.outbidLabel", "Outbid")}
            value={data?.outbid.length ?? 0}
            hint={t("dashboard.stat.outbidHint", "Live auctions to re-bid on")}
            to="/my-bids"
            accent="amber"
          />
          <StatCard
            icon={Trophy}
            label={t("dashboard.stat.wonLabel", "Auctions won")}
            value={data?.won.length ?? 0}
            hint={t("dashboard.stat.wonHint", "Completed wins")}
            to="/orders"
            accent="gold"
          />
          <StatCard
            icon={Heart}
            label={t("dashboard.stat.watchlistLabel", "Watchlist")}
            value={data?.watchAuctions.length ?? 0}
            hint={t("dashboard.stat.watchlistHint", "Cards you're tracking")}
            to="/watchlist"
            accent="sky"
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryTile
            icon={CreditCard}
            label={t("dashboard.summary.pendingPaymentsLabel", "Pending payments")}
            value={String(data?.pendingPayments.length ?? 0)}
            hint={t("dashboard.summary.pendingPaymentsHint", "Orders awaiting checkout")}
            tone={data?.pendingPayments.length ? "amber" : undefined}
          />
          <SummaryTile
            icon={TrendingUp}
            label={t("dashboard.summary.totalSpendLabel", "Total spend")}
            value={formatGBP(data?.totalSpendPence ?? 0)}
            hint={t("dashboard.summary.totalSpendHint", "Across paid orders")}
            tone="gold"
          />
          <SummaryTile
            icon={Package}
            label={t("dashboard.summary.ordersLabel", "Orders")}
            value={String(data?.orders.length ?? 0)}
            hint={t("dashboard.summary.ordersHint", "All-time order records")}
          />
          <SummaryTile
            icon={Store}
            label={t("dashboard.summary.listingsLabel", "My listings")}
            value={String(data?.listings ?? 0)}
            hint={t("dashboard.summary.listingsHint", "Cards you have listed")}
          />
        </div>

        {!!data?.pendingPayments.length && (
          <div className="mt-6 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                {t("dashboard.pendingBanner", "{count, plural, one {You have # order awaiting payment.} other {You have # orders awaiting payment.}}", { count: data.pendingPayments.length })}
              </span>
              <Button asChild size="sm" variant="outline" className="border-amber-500/40">
                <Link to="/orders">{t("dashboard.completePayment", "Complete payment")}</Link>
              </Button>
            </div>
          </div>
        )}

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <AuctionList
            title={t("dashboard.leadingListTitle", "Auctions you're leading")}
            empty={t("dashboard.leadingListEmpty", "You're not the highest bidder on any live auction right now.")}
            rows={data?.leading ?? []}
          />
          <AuctionList
            title={t("dashboard.outbidListTitle", "You've been outbid")}
            empty={t("dashboard.outbidListEmpty", "No outbid auctions — nicely held.")}
            rows={data?.outbid ?? []}
          />
        </div>

        <div className="mt-6">
          <AuctionList
            title={t("dashboard.watchlistListTitle", "Watchlist")}
            empty={t("dashboard.watchlistListEmpty", "Your watchlist is empty.")}
            rows={(data?.watchAuctions ?? []).slice(0, 6)}
            viewAll="/watchlist"
          />
        </div>

        <div className="mt-10">
          <StripeConnectCard />
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          <QuickLink to="/seller" title={t("dashboard.quickLink.sellerTitle", "Seller Dashboard")} hint={t("dashboard.quickLink.sellerHint", "Sales, earnings, payouts & performance")} />
          <QuickLink to="/my-bids" title={t("dashboard.quickLink.myBidsTitle", "My bids")} hint={t("dashboard.quickLink.myBidsHint", "Track your activity")} />
          <QuickLink to="/watchlist" title={t("dashboard.quickLink.watchlistTitle", "Watchlist")} hint={t("dashboard.quickLink.watchlistHint", "Cards you're following")} />
        </div>
      </div>
    </div>
  );
}

function SummaryTile({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof Gavel;
  label: string;
  value: string;
  hint: string;
  tone?: "gold" | "amber";
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="h-3 w-3" aria-hidden /> {label}
      </div>
      <p
        className={`mt-1.5 font-display text-2xl tabular-nums ${
          tone === "gold" ? "text-gold-gradient" : tone === "amber" ? "text-amber-400" : ""
        }`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function AuctionList({
  title,
  empty,
  rows,
  viewAll,
}: {
  title: string;
  empty: string;
  rows: AuctionRow[];
  viewAll?: "/watchlist";
}) {
  const { t } = useTranslation("settings");
  const viewAllLabel = t("dashboard.viewAll", "View all");
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-lg">{title}</h2>
        {viewAll && (
          <Link to={viewAll} className="text-xs text-muted-foreground hover:text-gold">
            {viewAllLabel}
          </Link>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="rounded-xl border border-border/50 bg-background/30 p-4 text-sm text-muted-foreground">
          {empty}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 6).map((a) => (
            <li key={a.id}>
              <Link
                to="/auction/$slug"
                params={{ slug: buildAuctionSlug(a) }}
                className="flex items-center gap-3 rounded-xl border border-white/10 bg-background/30 p-2.5 transition-colors hover:border-gold/40"
              >
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-black/40">
                  {a.images?.[0] && (
                    <img
                      src={a.images[0]}
                      alt=""
                      width={48}
                      height={48}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{a.title}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <StatusBadge auction={a} size="xs" />
                    <GradeBadge auction={a} size="xs" />
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-display text-base text-gold-gradient tabular-nums">
                    {formatGBP(a.current_bid_pence ?? a.starting_price_pence)}
                  </p>
                  <Countdown endTime={a.end_time} className="text-[11px]" />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function QuickLink({ to, title, hint }: { to: "/seller" | "/my-bids" | "/watchlist"; title: string; hint: string }) {
  return (
    <Link
      to={to}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/40 hover:bg-white/[0.05]"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-xl">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
        </div>
        <ArrowUpRight className="h-4 w-4 text-muted-foreground/40 transition-all duration-300 group-hover:text-gold group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
